import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Landmark, Plus, Search, Edit, Trash2, CheckCircle, XCircle, 
  AlertCircle, Play, Star, StarOff, Shield
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { 
  TableContainer, Table, TableHeader, TableBody, TableRow, 
  TableHead, TableCell, TableEmpty, TableLoading 
} from '../../components/ui/Table';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatDate } from '../../lib/utils';

interface BankConnection {
  id: string;
  tenantId: string;
  name: string;
  provider: 'OPENPIX' | 'IUGU' | 'ASAAS';
  isDefault: boolean;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
  lastTestedAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
  accountsCount?: number;
}

const providerOptions = [
  { value: '', label: 'Todos os providers' },
  { value: 'OPENPIX', label: 'OpenPix' },
  { value: 'IUGU', label: 'iugu' },
  { value: 'ASAAS', label: 'Asaas' },
];

const providerColors = {
  OPENPIX: 'text-green-400',
  IUGU: 'text-blue-400',
  ASAAS: 'text-purple-400',
};

const statusBadgeVariant = {
  ACTIVE: 'success' as const,
  INACTIVE: 'warning' as const,
  ERROR: 'error' as const,
};

export function AdminBankConnections() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<BankConnection | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    provider: 'OPENPIX' as 'OPENPIX' | 'IUGU' | 'ASAAS',
    apiKey: '',
    accountId: '',
    webhookSecret: '',
    isDefault: false,
  });

  // Fetch connections
  const { data: connections = [], isLoading, error: connectionsError } = useQuery({
    queryKey: ['bankConnections', providerFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (providerFilter) params.append('provider', providerFilter);
      const url = `${ENDPOINTS.bankConnections.list}${params.toString() ? `?${params}` : ''}`;
      const response = await api.get(url);
      return response.data;
    },
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403) return false;
      return failureCount < 2;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post(ENDPOINTS.bankConnections.create, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankConnections'] });
      setIsCreateModalOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; status?: string } }) =>
      api.put(ENDPOINTS.bankConnections.update(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankConnections'] });
      setIsEditModalOpen(false);
      setSelectedConnection(null);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(ENDPOINTS.bankConnections.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankConnections'] });
    },
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: (id: string) => api.post(ENDPOINTS.bankConnections.test(id)),
    onSuccess: (response) => {
      setTestResult(response.data as { success: boolean; message: string });
      queryClient.invalidateQueries({ queryKey: ['bankConnections'] });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      setTestResult({ success: false, message: error.response?.data?.message || 'Erro ao testar conexão' });
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => api.post(ENDPOINTS.bankConnections.setDefault(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankConnections'] });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      provider: 'OPENPIX',
      apiKey: '',
      accountId: '',
      webhookSecret: '',
      isDefault: false,
    });
  };

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleEdit = (connection: BankConnection) => {
    setSelectedConnection(connection);
    setIsEditModalOpen(true);
  };

  const handleUpdate = (name: string) => {
    if (!selectedConnection) return;
    updateMutation.mutate({ id: selectedConnection.id, data: { name } });
  };

  const handleDelete = (connection: BankConnection) => {
    if (connection.accountsCount && connection.accountsCount > 0) {
      alert(`Não é possível desativar: ${connection.accountsCount} conta(s) usando esta conexão`);
      return;
    }
    if (confirm(`Deseja desativar a conexão "${connection.name}"?`)) {
      deleteMutation.mutate(connection.id);
    }
  };

  const handleTest = (connection: BankConnection) => {
    setTestResult(null);
    testMutation.mutate(connection.id);
  };

  const handleSetDefault = (connection: BankConnection) => {
    if (connection.status !== 'ACTIVE') {
      alert('Apenas conexões ativas podem ser definidas como padrão');
      return;
    }
    setDefaultMutation.mutate(connection.id);
  };

  // Filter connections by search
  const filteredConnections = connections.filter((conn: BankConnection) => {
    const searchLower = search.toLowerCase();
    return conn.name.toLowerCase().includes(searchLower);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Conexões Bancárias</h1>
          <p className="text-surface-400">Gerencie as conexões com providers de pagamento</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Conexão
        </Button>
      </div>

      {/* Test Result Alert */}
      {testResult && (
        <Card className={testResult.success ? 'border-success/50 bg-success/10' : 'border-error/50 bg-error/10'}>
          <div className="flex items-center gap-3">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-success" />
            ) : (
              <AlertCircle className="w-5 h-5 text-error" />
            )}
            <div>
              <p className="font-medium">{testResult.success ? 'Conexão OK' : 'Falha na Conexão'}</p>
              <p className="text-sm text-surface-400">{testResult.message}</p>
            </div>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setTestResult(null)}>
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <Select
            options={providerOptions}
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="w-48"
          />
        </div>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Contas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Último Teste</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(connectionsError as any)?.response?.status === 403 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <Shield className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-surface-300 font-medium">Acesso restrito</p>
                    <p className="text-sm text-surface-500 mt-1">Você não tem permissão para visualizar conexões bancárias. Contate o administrador.</p>
                  </td>
                </tr>
              ) : isLoading ? (
                <TableLoading columns={6} rows={5} />
              ) : filteredConnections.length === 0 ? (
                <TableEmpty title="Nenhuma conexão encontrada" />
              ) : (
                filteredConnections.map((conn: BankConnection) => (
                  <TableRow key={conn.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Landmark className={`w-4 h-4 ${providerColors[conn.provider]}`} />
                        <span className="font-medium">{conn.name}</span>
                        {conn.isDefault && (
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="info">{conn.provider}</Badge>
                    </TableCell>
                    <TableCell>{conn.accountsCount || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {conn.status === 'ACTIVE' && <CheckCircle className="w-4 h-4 text-success" />}
                        {conn.status === 'INACTIVE' && <XCircle className="w-4 h-4 text-warning" />}
                        {conn.status === 'ERROR' && <AlertCircle className="w-4 h-4 text-error" />}
                        <Badge variant={statusBadgeVariant[conn.status]}>
                          {conn.status === 'ACTIVE' ? 'Ativo' : conn.status === 'INACTIVE' ? 'Inativo' : 'Erro'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {conn.lastTestedAt ? (
                          <span className="text-sm">{formatDate(conn.lastTestedAt)}</span>
                        ) : (
                          <span className="text-surface-400 text-sm">Nunca testado</span>
                        )}
                        {conn.lastError && (
                          <p className="text-xs text-error truncate max-w-[200px]" title={conn.lastError}>
                            {conn.lastError}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleTest(conn)}
                          disabled={testMutation.isPending}
                          title="Testar conexão"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        {!conn.isDefault && conn.status === 'ACTIVE' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSetDefault(conn)}
                            title="Definir como padrão"
                          >
                            <StarOff className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(conn)} title="Editar">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {conn.status === 'ACTIVE' && !conn.isDefault && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(conn)} title="Desativar">
                            <Trash2 className="w-4 h-4 text-error" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nova Conexão Bancária"
        size="md"
      >
        <CreateConnectionForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Conexão"
        size="sm"
      >
        {selectedConnection && (
          <EditConnectionForm
            connection={selectedConnection}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditModalOpen(false)}
            isLoading={updateMutation.isPending}
          />
        )}
      </Modal>
    </div>
  );
}

// Default export for compatibility
export default AdminBankConnections;


// Form data type
type ConnectionFormData = {
  name: string;
  provider: 'OPENPIX' | 'IUGU' | 'ASAAS';
  apiKey: string;
  accountId: string;
  webhookSecret: string;
  isDefault: boolean;
};

// Create Connection Form
interface CreateConnectionFormProps {
  formData: ConnectionFormData;
  setFormData: (data: ConnectionFormData | ((prev: ConnectionFormData) => ConnectionFormData)) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CreateConnectionForm({ formData, setFormData, onSubmit, onCancel, isLoading }: CreateConnectionFormProps) {
  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev: ConnectionFormData) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <Input
        label="Nome da Conexão"
        placeholder="Ex: OpenPix Principal"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
      />

      <Select
        label="Provider"
        options={[
          { value: 'OPENPIX', label: 'OpenPix' },
          { value: 'IUGU', label: 'iugu' },
          { value: 'ASAAS', label: 'Asaas' },
        ]}
        value={formData.provider}
        onChange={(e) => handleChange('provider', e.target.value)}
      />

      <Input
        label="API Key"
        type="password"
        placeholder="Sua API Key do provider"
        value={formData.apiKey}
        onChange={(e) => handleChange('apiKey', e.target.value)}
      />

      {formData.provider === 'IUGU' && (
        <Input
          label="Account ID (opcional)"
          placeholder="ID da conta no iugu"
          value={formData.accountId}
          onChange={(e) => handleChange('accountId', e.target.value)}
        />
      )}

      <Input
        label="Webhook Secret (opcional)"
        type="password"
        placeholder="Secret para validar webhooks"
        value={formData.webhookSecret}
        onChange={(e) => handleChange('webhookSecret', e.target.value)}
      />

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.isDefault}
          onChange={(e) => handleChange('isDefault', e.target.checked)}
          className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
        />
        <span className="text-sm">Definir como conexão padrão</span>
      </label>

      <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={onSubmit} disabled={isLoading || !formData.name || !formData.apiKey}>
          {isLoading ? 'Criando...' : 'Criar Conexão'}
        </Button>
      </div>
    </div>
  );
}

// Edit Connection Form
interface EditConnectionFormProps {
  connection: BankConnection;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function EditConnectionForm({ connection, onSubmit, onCancel, isLoading }: EditConnectionFormProps) {
  const [name, setName] = useState(connection.name);

  return (
    <div className="space-y-4">
      <div className="p-3 bg-surface-800 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Landmark className={`w-4 h-4 ${providerColors[connection.provider]}`} />
          <Badge variant="info">{connection.provider}</Badge>
          {connection.isDefault && (
            <Badge variant="warning">Padrão</Badge>
          )}
        </div>
        <p className="text-xs text-surface-400">
          Criado em {formatDate(connection.createdAt)}
        </p>
      </div>

      <Input
        label="Nome da Conexão"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={() => onSubmit(name)} disabled={isLoading || !name}>
          {isLoading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}
